import { makeAutoObservable, runInAction, computed } from "mobx";
import { notificationsService } from "../services";
import { showToast } from "@/lib/utils/toast";

const createBulkMeta = () => ({ markAll: null, clearAll: null });

const resolvePayload = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
};

const serializeState = (state) => {
  if (!state || typeof state !== "object") return null;

  try {
    return JSON.parse(JSON.stringify(state));
  } catch {
    return null;
  }
};

const normalizeNotification = (incoming) => {
  if (!incoming || typeof incoming !== "object") return null;
  const candidateId =
    incoming.id ||
    incoming._id ||
    (incoming.target?.id && incoming.createdAt
      ? `${incoming.target.id}:${incoming.createdAt}`
      : null);

  return {
    ...incoming,
    id: candidateId,
    createdAt: incoming.createdAt || new Date().toISOString(),
  };
};

class NotificationsStore {
  items = [];
  unread = 0;
  loading = false;
  error = "";
  loaded = false;
  bulkMeta = createBulkMeta();
  userId = null;

  constructor() {
    makeAutoObservable(this, {
      hasItems: computed,
    });
  }

  get hasItems() {
    return this.items.length > 0;
  }

  setUserContext = (userId) => {
    const resolved = userId || null;
    if (resolved === this.userId) return;
    this.userId = resolved;
    this.reset();
  };

  reset = () => {
    this.items = [];
    this.unread = 0;
    this.loading = false;
    this.error = "";
    this.loaded = false;
    this.bulkMeta = createBulkMeta();
  };

  recalcUnread = () => {
    this.unread = this.items.reduce(
      (count, note) => (note?.readAt ? count : count + 1),
      0,
    );
  };

  loadNotifications = async ({ limit = 100, force = false } = {}) => {
    if (!force && (this.loading || this.loaded)) return;

    runInAction(() => {
      this.loading = true;
      this.error = "";
    });

    try {
      const data = await notificationsService.listNotifications({
        limit,
      });

      if (!data) {
        runInAction(() => {
          this.items = [];
          this.unread = 0;
          this.loaded = true;
          this.error = "Unable to load notifications.";
        });
        return;
      }

      const payload = resolvePayload(data) || {};
      const items = Array.isArray(payload.items)
        ? payload.items
        : Array.isArray(payload.data?.items)
          ? payload.data.items
          : [];
      const normalized = items.map(normalizeNotification).filter(Boolean);

      runInAction(() => {
        this.items = normalized;
        this.recalcUnread();
        this.loaded = true;
      });
    } catch (error) {
      runInAction(() => {
        this.items = [];
        this.unread = 0;
        this.loaded = true;
        this.error = error?.message || "Unable to load notifications.";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  handleIncomingNotification = (incoming, options = {}) => {
    const normalized = normalizeNotification(incoming);
    if (!normalized) return;

    const shouldToast = options.toast !== false;

    runInAction(() => {
      const existingIndex = this.items.findIndex(
        (item) => item?.id && item.id === normalized.id,
      );
      if (existingIndex !== -1) {
        const next = [...this.items];
        next.splice(existingIndex, 1);
        this.items = [normalized, ...next];
      } else {
        this.items = [normalized, ...this.items];
      }
      this.recalcUnread();
    });

    if (shouldToast && normalized?.message) {
      showToast.info(normalized.message);
    }
  };

  removeById = async (id) => {
    if (!id) return;

    let removedWasUnread = false;
    runInAction(() => {
      const removed = this.items.find((note) => note?.id === id);
      removedWasUnread = Boolean(removed && !removed.readAt);
      this.items = this.items.filter((note) => note?.id !== id);
      if (removedWasUnread) {
        this.unread = Math.max(this.unread - 1, 0);
      }
    });

    try {
      await notificationsService.remove(id);
    } catch (error) {
      // Keep UI optimistic even if the request fails.
    }
  };

  markReadById = async (id) => {
    if (!id) return;

    runInAction(() => {
      let updated = false;
      this.items = this.items.map((note) => {
        if (!note || note.id !== id || note.readAt) return note;
        updated = true;
        return { ...note, readAt: new Date().toISOString() };
      });

      if (updated) {
        this.unread = Math.max(this.unread - 1, 0);
      }
    });

    try {
      const data = await notificationsService.markRead(id);
      return Boolean(data);
    } catch (error) {
      return false;
    }
  };

  markAllRead = async () => {
    const snapshotUnread = this.unread;
    const snapshotTotal = this.items.length;

    runInAction(() => {
      this.items = this.items.map((item) => ({
        ...item,
        readAt: item.readAt || new Date().toISOString(),
      }));
      this.unread = 0;
    });

    try {
      const data = await notificationsService.markAllRead();
      const payload = resolvePayload(data) || {};
      if (!data) throw new Error("Unable to mark notifications.");

      const meta = {
        updated: payload.updated ?? snapshotUnread,
        readAt: payload.readAt || new Date().toISOString(),
        beforeUnread: snapshotUnread,
        total: snapshotTotal,
      };

      runInAction(() => {
        this.bulkMeta = { ...this.bulkMeta, markAll: meta };
      });

      return meta;
    } catch (error) {
      return null;
    }
  };

  clearAll = async () => {
    const snapshotTotal = this.items.length;

    runInAction(() => {
      this.items = [];
      this.unread = 0;
    });

    try {
      const data = await notificationsService.clearAll();
      const payload = resolvePayload(data) || {};
      if (!data) throw new Error("Unable to clear notifications.");

      const meta = {
        cleared: payload.cleared ?? snapshotTotal,
        archivedAt: payload.archivedAt || new Date().toISOString(),
        total: snapshotTotal,
      };

      runInAction(() => {
        this.bulkMeta = { ...this.bulkMeta, clearAll: meta };
      });

      return meta;
    } catch (error) {
      return null;
    }
  };

  resolveNotificationTarget = (note) => {
    if (!note) return null;

    const action = note?.action || null;
    if (action?.url) {
      return {
        url: action.url,
        state: serializeState(action.state || action.payload),
        label: action.label || null,
        markRead: action.markRead !== false,
      };
    }

    const type = note?.type;
    /* if (type === "follow_up_assigned") {
       return {
         url: "/dashboard?followUp=true",
         state: serializeState({
           followUpId:
             note?.meta?.followUpId ||
             note?.meta?.id ||
             note?.meta?.followUpRequestId ||
             note?.action?.payload?.followUpId ||
             null,
         }),
       };
     }  */

    if (type === "Carbon.inventory_submitted") {
      const orgId =
        note?.meta?.organizationUnitId ||
        note?.meta?.orgId ||
        note?.action?.payload?.organizationUnitId;
      const year =
        note?.meta?.reportingYear ||
        note?.meta?.year ||
        note?.action?.payload?.reportingYear;
      if (!orgId || !year) return null;
      return {
        url: `/carbon/submissions/${encodeURIComponent(orgId)}/${encodeURIComponent(String(year))}`,
        state: serializeState({
          submissionId:
            note?.meta?.submissionId ||
            note?.action?.payload?.submissionId ||
            null,
        }),
      };
    }

    if (type === "Carbon.consent_requested") {
      const year = note?.meta?.reportingYear || note?.meta?.year;
      const requestId =
        note?.meta?.requestId ||
        note?.meta?.consentRequestId ||
        note?.action?.payload?.requestId;
      if (!year || !requestId) return null;
      return {
        url: `/carbon/survey/${encodeURIComponent(String(year))}?consent=${encodeURIComponent(
          requestId,
        )}`,
        state: serializeState({
          requestId,
          submissionId:
            note?.meta?.submissionId ||
            note?.action?.payload?.submissionId ||
            null,
        }),
      };
    }

    if (type === "Carbon.consent_approved") {
      const orgId =
        note?.meta?.organizationUnitId ||
        note?.meta?.orgId ||
        note?.action?.payload?.organizationUnitId;
      const year =
        note?.meta?.reportingYear ||
        note?.meta?.year ||
        note?.action?.payload?.reportingYear;
      if (!orgId || !year) return null;
      return {
        url: `/carbon/submissions/${encodeURIComponent(orgId)}/${encodeURIComponent(String(year))}`,
        state: serializeState({
          submissionId:
            note?.meta?.submissionId ||
            note?.action?.payload?.submissionId ||
            null,
        }),
      };
    }

    return null;
  };
}

export const notificationsStore = new NotificationsStore();
